import Phaser from 'phaser';

/**
 * PreloaderScene - Loads all game assets
 * Displays loading progress to the player
 */
export class PreloaderScene extends Phaser.Scene {
  private progressBar!: Phaser.GameObjects.Graphics;
  private progressBox!: Phaser.GameObjects.Graphics;
  private loadingText!: Phaser.GameObjects.Text;
  private percentText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'PreloaderScene' });
  }

  preload(): void {
    this.createLoadingUI();
    this.setupLoadingEvents();
    this.loadAssets();
  }

  create(): void {
    // Clean up loading UI
    this.progressBar.destroy();
    this.progressBox.destroy();
    this.loadingText.destroy();
    this.percentText.destroy();

    // Proceed to main menu
    this.scene.start('MainMenuScene');
  }

  private createLoadingUI(): void {
    const width = this.cameras.main.width;
    const height = this.cameras.main.height;

    // Progress box background
    this.progressBox = this.add.graphics();
    this.progressBox.fillStyle(0x222222, 0.8);
    this.progressBox.fillRect(width / 2 - 160, height / 2 - 25, 320, 50);

    // Progress bar (fills as loading progresses)
    this.progressBar = this.add.graphics();

    // Loading text
    this.loadingText = this.add.text(width / 2, height / 2 - 50, 'Loading...', {
      fontFamily: 'monospace',
      fontSize: '20px',
      color: '#ffffff',
    });
    this.loadingText.setOrigin(0.5, 0.5);

    // Percentage text
    this.percentText = this.add.text(width / 2, height / 2, '0%', {
      fontFamily: 'monospace',
      fontSize: '18px',
      color: '#ffffff',
    });
    this.percentText.setOrigin(0.5, 0.5);
  }

  private setupLoadingEvents(): void {
    this.load.on('progress', (value: number) => {
      this.progressBar.clear();
      this.progressBar.fillStyle(0x4ade80, 1); // Solarpunk green
      this.progressBar.fillRect(
        this.cameras.main.width / 2 - 150,
        this.cameras.main.height / 2 - 15,
        300 * value,
        30
      );
      this.percentText.setText(`${Math.round(value * 100)}%`);
    });

    this.load.on('fileprogress', (file: Phaser.Loader.File) => {
      this.loadingText.setText(`Loading: ${file.key}`);
    });
  }

  private loadAssets(): void {
    // Load game data JSON files
    this.load.json('beasties', 'assets/data/beasties.json');
    this.load.json('abilities', 'assets/data/abilities.json');
    this.load.json('zones', 'assets/data/zones.json');

    // Load audio
    this.load.audio('menu_music', 'assets/audio/menu_music.mp3');

    // Load tilemap (as tilemap for GameScene)
    this.load.tilemapTiledJSON('meadowvale_test', 'assets/maps/meadowvale_test.json');
    // Also load as JSON for MapEditorScene
    this.load.json('meadowvale_test', 'assets/maps/meadowvale_test.json');

    // Create placeholder assets programmatically
    this.createPlaceholderSprites();
    this.createPlaceholderTileset();

    // TODO: Load actual game assets when available
    // this.load.image('tileset', 'assets/images/tilesets/solarpunk_terrain.png');
    // this.load.atlas('player', 'assets/images/characters/player.png', 'assets/images/characters/player.json');
    // this.load.atlas('beasties', 'assets/images/beasties/beasties.png', 'assets/images/beasties/beasties.json');
  }

  /**
   * Create placeholder sprite textures for testing
   * These will be replaced with real art assets later
   */
  private createPlaceholderSprites(): void {
    const graphics = this.make.graphics({ x: 0, y: 0 });

    // Player placeholder - yellow/gold humanoid shape
    graphics.clear();
    // Body
    graphics.fillStyle(0xfbbf24, 1);
    graphics.fillCircle(16, 12, 8); // Head
    graphics.fillRect(10, 18, 12, 16); // Body
    // Outline
    graphics.lineStyle(2, 0xf59e0b);
    graphics.strokeCircle(16, 12, 8);
    graphics.strokeRect(10, 18, 12, 16);
    graphics.generateTexture('player_placeholder', 32, 40);

    // Solara (Solar Fox) placeholder - orange/gold fox shape
    graphics.clear();
    // Body
    graphics.fillStyle(0xfb923c, 1);
    graphics.fillEllipse(16, 18, 22, 14); // Body
    graphics.fillCircle(26, 12, 6); // Head
    // Ears
    graphics.fillTriangle(24, 6, 28, 6, 26, 2);
    graphics.fillTriangle(28, 8, 32, 8, 30, 4);
    // Tail
    graphics.fillStyle(0xfde047, 1);
    graphics.fillEllipse(4, 16, 8, 6);
    // Outline
    graphics.lineStyle(1, 0xea580c);
    graphics.strokeEllipse(16, 18, 22, 14);
    graphics.strokeCircle(26, 12, 6);
    graphics.generateTexture('beastie_solara', 36, 28);

    // Generic beastie placeholder - cyan circle
    graphics.clear();
    graphics.fillStyle(0x22d3ee, 1);
    graphics.fillCircle(14, 14, 12);
    graphics.lineStyle(2, 0x0891b2);
    graphics.strokeCircle(14, 14, 12);
    // Eyes
    graphics.fillStyle(0x000000, 1);
    graphics.fillCircle(10, 12, 2);
    graphics.fillCircle(18, 12, 2);
    graphics.generateTexture('beastie_placeholder', 28, 28);

    // NPC placeholder - purple humanoid
    graphics.clear();
    graphics.fillStyle(0xa855f7, 1);
    graphics.fillCircle(16, 12, 8);
    graphics.fillRect(10, 18, 12, 16);
    graphics.lineStyle(2, 0x7c3aed);
    graphics.strokeCircle(16, 12, 8);
    graphics.strokeRect(10, 18, 12, 16);
    graphics.generateTexture('npc_placeholder', 32, 40);

    // Enemy placeholder (training dummy) - red circle with X
    graphics.clear();
    graphics.fillStyle(0xef4444, 1);
    graphics.fillCircle(16, 16, 14);
    graphics.lineStyle(3, 0xb91c1c);
    graphics.strokeCircle(16, 16, 14);
    // X marks the spot
    graphics.lineStyle(3, 0xffffff);
    graphics.lineBetween(10, 10, 22, 22);
    graphics.lineBetween(22, 10, 10, 22);
    graphics.generateTexture('enemy_placeholder', 32, 32);

    graphics.destroy();
  }

  /**
   * Create placeholder tileset texture for testing
   * Creates a strip of isometric tiles: border, grass, grass_alt, path
   */
  private createPlaceholderTileset(): void {
    const tileWidth = 64;
    const tileHeight = 32;
    const tileCount = 4;

    const graphics = this.make.graphics({ x: 0, y: 0 });

    // Tile 1: Border/Wall (dark gray)
    this.drawIsometricTile(graphics, 0, 0, tileWidth, tileHeight, 0x4a5568, 0x2d3748);

    // Tile 2: Grass (green)
    this.drawIsometricTile(graphics, tileWidth, 0, tileWidth, tileHeight, 0x3d7a5f, 0x2d5a47);

    // Tile 3: Path (tan)
    this.drawIsometricTile(graphics, tileWidth * 2, 0, tileWidth, tileHeight, 0xd4a574, 0xb8956a);

    // Tile 4: Collision marker (red, for debugging)
    this.drawIsometricTile(graphics, tileWidth * 3, 0, tileWidth, tileHeight, 0xef4444, 0xb91c1c);

    // Generate the tileset texture
    graphics.generateTexture('terrain_tileset', tileWidth * tileCount, tileHeight);
    graphics.destroy();
  }

  /**
   * Draw a single isometric tile at the given position
   */
  private drawIsometricTile(
    graphics: Phaser.GameObjects.Graphics,
    x: number,
    y: number,
    width: number,
    height: number,
    fillColor: number,
    strokeColor: number
  ): void {
    const centerX = x + width / 2;
    const centerY = y + height / 2;

    graphics.fillStyle(fillColor, 1);
    graphics.lineStyle(1, strokeColor, 0.8);

    graphics.beginPath();
    graphics.moveTo(centerX, y);                    // Top
    graphics.lineTo(x + width, centerY);            // Right
    graphics.lineTo(centerX, y + height);           // Bottom
    graphics.lineTo(x, centerY);                    // Left
    graphics.closePath();
    graphics.fillPath();
    graphics.strokePath();
  }
}
